<template>
  <el-dialog
    @close="onDialogClosing"
    :width="'100%'"
    :fullscreen="true"
    :destroy-on-close="true"
    :lock-scroll="true"
    :close-on-click-modal="false"
    v-model="syncedModel"
    custom-class="dialog-ocr"
  >
    <template #title>
      <div class="flex justify-center items-center">
        <div class="label-round">{{ $t('take_photo') }}</div>
      </div>
    </template>

    <div class="camera-container">
      <video id="video" ref="cameraVideo" playsinline autoplay></video>
      <img v-if="overlayImage" :src="overlayImage" class="overlay-image" style="object-fit: contain" />
      <canvas ref="cameraCanvas" style="display: none"></canvas>
    </div>

    <div class="step-button">
      <el-button
        :disabled="!isCaptured"
        type="primary"
        class="take-photo-action-button"
        @click="retakePhoto"
      >
        <IconArrowBack />
        <KeyenceHTHardKeyButton
          v-if="isKeyenceEnterpriseBrowser"
          :hardkey="KeyenceHardKey.BACKSPACE"
          :function="retakePhoto"
          :visible="!!syncedModel"
          :disabled="!isCaptured"
          :newLayer="false"
        />
      </el-button>
      <el-button
        v-if="!isCaptured"
        type="primary"
        class="take-photo-action-button"
        @click="onCaptureClicked"
      >
        <IconCamera class="take-photo-camera-icon" />
      </el-button>
      <el-button
        v-else
        :disabled="!imageCaptured"
        type="primary"
        class="take-photo-action-button"
        @click="confirm"
      >
        {{ $t('OK') }}
        <KeyenceHTHardKeyButton
          v-if="isKeyenceEnterpriseBrowser"
          :hardkey="KeyenceHardKey.ENTER"
          :function="confirm"
          :visible="!!syncedModel && isCaptured"
          :disabled="!imageCaptured"
          :newLayer="true"
        />
      </el-button>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import IconCamera from '@/components/svg/IconCamera.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import { mobileCheck, stopStreams } from '@/utils/helpers'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { Options, Vue } from 'vue-class-component'
import { Prop, PropSync, Watch } from 'vue-property-decorator'

@Options({
  name: 'ActionDialog',
  components: {
    IconCamera,
    IconArrowBack,
    KeyenceHTHardKeyButton,
  },
  emits: ['update:isShowActionDialog', 'update:captureImage'],
})
export default class CustomTakePhotoCamera extends Vue {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  @PropSync('isShowActionDialog', { type: Boolean }) syncedModel?: boolean
  @Prop({ type: String }) readonly overlayImage?: string
  isCaptured = false
  imageCaptured: null | Blob = null
  stream: undefined | MediaStream = undefined
  video: HTMLVideoElement | null = null

  @Watch('syncedModel')
  onDialogChanged() {
    if (!this.syncedModel) return
    this.$nextTick(() => this.initCamera())
  }

  async initCamera() {
    try {
      if (!this.video) {
        this.video = document.getElementById('video') as HTMLVideoElement
      }
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', aspectRatio: mobileCheck() ? 16 / 9 : 9 / 16 },
      })

      this.$refs.cameraVideo.srcObject = this.stream
    } catch (error) {
      console.error('Error accessing media devices.', error)
    }
  }

  vidOff() {
    if (this.stream) {
      stopStreams([this.stream])
    }
  }

  beforeUnmount() {
    this.vidOff()
  }

  retakePhoto() {
    this.imageCaptured = null
    this.isCaptured = false
    this.$refs.cameraVideo.play()
  }

  onCaptureClicked() {
    this.isCaptured = true
    const video = this.$refs.cameraVideo
    const canvas = this.$refs.cameraCanvas

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Do something with the captured image, e.g. save to server or display on page
    canvas.toBlob((blob: Blob) => (this.imageCaptured = blob), 'image/png')
    this.$refs.cameraVideo.pause()
  }

  confirm() {
    this.$emit('update:captureImage', this.imageCaptured)
    this.resetState()
  }

  onDialogClosing() {
    this.$refs.cameraVideo.srcObject = null
    if (this.stream) this.stream.getTracks()[0].stop()
    this.syncedModel = false
  }

  resetState() {
    this.syncedModel = false
    this.isCaptured = false
    this.imageCaptured = null
    this.stream = undefined
    this.video = null
  }
}
</script>
<style scoped>
.step-button {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 16px 8px;
  width: 100%;
  box-sizing: border-box;
}

.take-photo-action-button {
  width: 100%;
  min-height: 60px;
  margin: 0;
  font-size: 22px;
}

.take-photo-camera-icon {
  width: 2rem;
  height: 2rem;
}

.camera-container {
  position: relative;
  width: 100%;
  height: 570px;
  /* padding-bottom: 75%; */
}

.camera-container video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 570px;
  object-fit: cover;
  z-index: 0;
}
.camera-container img {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  z-index: 1;
}
</style>
